// keytail-web 全局 AccountSDK 单例（P8 统一登录 SSO）。
//
// 接入模式 = plan 22 轻 BFF 跨子域 SSO（与 beemail-web / admin-console 一致）：
//   - 首屏 main.ts mount 前 await sdk.bootstrap()：凭 account 域 host-only 会话 cookie
//     `__lumenlan_sess` 调 ${baseURL}/v1/auth/session/token 静默换 access → **零 redirect 进入**。
//   - 无会话（真首登 / 换浏览器）才回落 PKCE：sdk.startPKCE() → 跳 ${baseURL}/v1/auth/authorize
//     → account portal 渲染统一登录页 → /callback?code+state → sdk.exchangePKCE() 换 token。
//   - access token **仅内存**；**不再持久化 refresh token**（见 account-sdk.ts 顶注）。
//
// product='keytail'、clientID='keytailapp'（[product][type] 直拼、无连字符，与应用域 keytailapp 一致、
// 同中央 D10 域名约定；lumenlan-platform 仓 oauth-clients.yaml 注册同名 client，其 allowed_redirect_uris
// host 即 session/token 端点的 Origin 白名单来源——子域 SSO 生效前提，见 plan 08 §跨仓依赖）。
// scope=['identity','keytail']（产品级 scope，同 beemail 的 ['identity','beemail'] 形态）。
// baseURL 按「当前站点域名」运行时派生（见 detectBaseURL），不构建期静态注入——同一份产物在
// soundlan / lumenlan 任一域自适配，杜绝「产物焊死另一环境 account 域」这类复发事故。

import { AccountSDK } from './account-sdk'
import { setTokenProvider } from '../api'

// localhost / IP（本地 dev、无可派生根域）时的回落——本地开发默认连真账号（生产 account）。
const DEFAULT_BASE = 'https://account.lumenlan.com'

// account 中心域名按当前站点 host 的根域（最后两段）拼 account 子域，零硬编码、零环境枚举：
//   keytailapp.soundlan.com → account.soundlan.com  （测试）
//   keytailapp.lumenlan.com → account.lumenlan.com  （生产）
// VITE_LUMENLAN_ACCOUNT_URL 仅作本地 dev 的显式逃生阀（默认不设；设了则优先）——沿用本仓
// api.ts 既有 env 名，避免再引一个新变量。
export function detectBaseURL(hostname?: string): string {
  const fromEnv = (import.meta.env.VITE_LUMENLAN_ACCOUNT_URL as string | undefined)?.trim()
  if (fromEnv) return fromEnv.replace(/\/$/, '')
  const host = hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '')
  // 归一化：小写 + 滤掉尾点 FQDN / 连续点空段，避免 'x.soundlan.com.' 派生出 'account.com.'。
  const parts = host.toLowerCase().split('.').filter(Boolean)
  // 至少两段且 TLD 段非纯数字（排除 IP 字面量）→ 同根域的 account 子域。
  if (parts.length >= 2 && !/^\d+$/.test(parts[parts.length - 1])) {
    return `https://account.${parts.slice(-2).join('.')}`
  }
  return DEFAULT_BASE // localhost / IP / 空 host
}

function detectClientID(): string {
  // 缺省回落 'keytailapp'（[product][type] 直拼，与 platform 仓 oauth-clients.yaml 预注册名一致）。
  const fromEnv = (import.meta.env.VITE_OAUTH_CLIENT_ID as string | undefined)?.trim()
  return fromEnv || 'keytailapp'
}

function ensureDeviceId(): string {
  const key = 'lumenlan.keytail.deviceId'
  try {
    const existing = localStorage.getItem(key)
    if (existing) return existing
    const fresh = crypto.randomUUID()
    localStorage.setItem(key, fresh)
    return fresh
  } catch {
    // localStorage 不可用（隐私模式 / SSR）：退化为本次会话内的临时 id。
    return crypto.randomUUID()
  }
}

export const baseURL = detectBaseURL()

// 账号中心域名（从 baseURL 派生），给登录页文案用。
export const accountHost = (() => {
  try {
    return new URL(baseURL).host
  } catch {
    return 'account.lumenlan.com'
  }
})()

// keytail 申请的 OAuth scope：身份 + 产品级 keytail。
export const KEYTAIL_SCOPES = ['identity', 'keytail']

export const sdk = new AccountSDK({
  baseURL,
  product: 'keytail',
  clientID: detectClientID(),
  deviceId: ensureDeviceId(),
  onUnauthorized: () => {
    // 会话失效（30 天过期 / 被吊销 / 登出）：跳品牌登录页（避免在 /login、/callback 里再弹）。
    if (typeof window !== 'undefined') {
      const p = window.location.pathname
      if (!p.startsWith('/login') && !p.startsWith('/callback')) {
        window.location.assign('/login?reason=expired')
      }
    }
  },
})

// 把 keytail-server 的 Bearer 调用桥接到 SDK 的内存 access token：api.ts 每次请求经 provider
// 拉当前 token（SDK scheduleRefresh 已主动保鲜），无需在刷新时手动回灌。SSO 是 keytail-server
// 鉴权的唯一 token 来源后，api.ts 旧的静态 setToken 仅留作 loginPhone 回落路径用（ADR D）。
setTokenProvider(() => sdk.currentAccessToken() ?? '')

export function isAuthed(): boolean {
  return !!sdk.currentUser()
}

export type { User, TokenSet } from './account-sdk'
